// LT-159 / LT-179: API client for the Sequence Engine endpoints.
// Mirrors backend/routers/sequences.py + sequence_enrollments.py.
import axios, { AxiosError } from 'axios';

import type {
  Sequence,
  SequenceActivateResponse,
  SequenceCreate,
  SequenceDashboard,
  SequenceEnrollment,
  SequenceEnrollmentConflictResponse,
  SequenceEnrollmentListResponse,
  SequenceStep,
  SequenceStepCreate,
  SequenceStepUpdate,
  SequenceUpdate,
  ContactActiveEnrollmentResponse,
} from '../types/sequence';


const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'http://localhost:8000';

const client = axios.create({ baseURL: API_BASE_URL, timeout: 30000 });

client.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers = config.headers || {};
    (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }
  return config;
});


export const sequencesAPI = {
  list: async (params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<Sequence[]> => {
    const r = await client.get<Sequence[]>('/sequences/', { params });
    return r.data;
  },

  get: async (id: string): Promise<Sequence> => {
    const r = await client.get<Sequence>(`/sequences/${id}`);
    return r.data;
  },

  create: async (payload: SequenceCreate): Promise<Sequence> => {
    const r = await client.post<Sequence>('/sequences/', payload);
    return r.data;
  },

  update: async (id: string, patch: SequenceUpdate): Promise<Sequence> => {
    const r = await client.patch<Sequence>(`/sequences/${id}`, patch);
    return r.data;
  },

  archive: async (id: string): Promise<void> => {
    await client.delete(`/sequences/${id}`);
  },

  activate: async (id: string): Promise<SequenceActivateResponse> => {
    const r = await client.post<SequenceActivateResponse>(
      `/sequences/${id}/activate`,
    );
    return r.data;
  },

  pause: async (id: string): Promise<Sequence> => {
    const r = await client.post<Sequence>(`/sequences/${id}/pause`);
    return r.data;
  },

  resume: async (id: string): Promise<Sequence> => {
    const r = await client.post<Sequence>(`/sequences/${id}/resume`);
    return r.data;
  },

  duplicate: async (id: string): Promise<Sequence> => {
    const r = await client.post<Sequence>(`/sequences/${id}/duplicate`);
    return r.data;
  },

  // LT-512: clone-to-edit. Functionally the same as `duplicate` server-side
  // (the new endpoint just makes the canvas "Clone" intent explicit), and
  // the returned row is always `is_system_managed=false`.
  clone: async (id: string): Promise<Sequence> => {
    const r = await client.post<Sequence>(`/sequences/${id}/clone`);
    return r.data;
  },

  // LT-261 / LT-170 — aggregated dashboard (enrollment totals + per-step
  // counts, outcomes, email engagement).
  dashboard: async (id: string): Promise<SequenceDashboard> => {
    const r = await client.get<SequenceDashboard>(`/sequences/${id}/dashboard`);
    return r.data;
  },

  // ---- Steps -----------------------------------------------------------

  listSteps: async (sequenceId: string): Promise<SequenceStep[]> => {
    const r = await client.get<SequenceStep[]>(`/sequences/${sequenceId}/steps`);
    return r.data;
  },

  addStep: async (
    sequenceId: string,
    payload: SequenceStepCreate,
  ): Promise<SequenceStep> => {
    const r = await client.post<SequenceStep>(
      `/sequences/${sequenceId}/steps`,
      payload,
    );
    return r.data;
  },

  updateStep: async (
    sequenceId: string,
    stepId: string,
    patch: SequenceStepUpdate,
  ): Promise<SequenceStep> => {
    const r = await client.patch<SequenceStep>(
      `/sequences/${sequenceId}/steps/${stepId}`,
      patch,
    );
    return r.data;
  },

  removeStep: async (sequenceId: string, stepId: string): Promise<void> => {
    await client.delete(`/sequences/${sequenceId}/steps/${stepId}`);
  },

  // LT-298 / LT-282 C2 — validate + new-version API. LT-302 / LT-303 use
  // these from the canvas UI; included here so the single API surface
  // stays in one module.
  validate: async (
    sequenceId: string,
  ): Promise<{
    errors: Array<{
      code: string;
      message: string;
      step_id?: string;
      step_position?: number;
      field_path?: string;
    }>;
    warnings: Array<{
      code: string;
      message: string;
      step_id?: string;
      step_position?: number;
    }>;
  }> => {
    const r = await client.post(`/sequences/${sequenceId}/validate`);
    return r.data;
  },

  newVersion: async (sequenceId: string): Promise<Sequence> => {
    const r = await client.post<Sequence>(
      `/sequences/${sequenceId}/new-version`,
    );
    return r.data;
  },

  // ---- Enrollments -----------------------------------------------------

  listEnrollments: async (
    sequenceId: string,
    params?: { status?: string; limit?: number; offset?: number },
  ): Promise<SequenceEnrollmentListResponse> => {
    const r = await client.get<SequenceEnrollmentListResponse>(
      `/sequences/${sequenceId}/enrollments`,
      { params },
    );
    return r.data;
  },

  /**
   * Returns the new enrollment on success.
   * On 409 (contact already active in another sequence and `force=false`),
   * throws an axios error whose `response.data` is a
   * SequenceEnrollmentConflictResponse — caller surfaces the auto-pause
   * confirmation dialog.
   */
  enrollContact: async (
    sequenceId: string,
    contactId: string,
    force = false,
  ): Promise<SequenceEnrollment> => {
    const r = await client.post<SequenceEnrollment>(
      `/sequences/${sequenceId}/enrollments`,
      { contact_id: contactId, force },
    );
    return r.data;
  },

  removeEnrollment: async (
    sequenceId: string,
    enrollmentId: string,
  ): Promise<void> => {
    await client.delete(
      `/sequences/${sequenceId}/enrollments/${enrollmentId}`,
    );
  },

  rerunEnrollmentFilter: async (sequenceId: string): Promise<{ enrolled_count: number }> => {
    const r = await client.post<{ enrolled_count: number }>(
      `/sequences/${sequenceId}/enrollments/rerun-filter`,
    );
    return r.data;
  },
};


/**
 * Helper to extract a 409 conflict body from an axios error during
 * enrollment, for the auto-pause confirmation dialog.
 */
export function extractEnrollmentConflict(
  err: unknown,
): SequenceEnrollmentConflictResponse | null {
  if (!axios.isAxiosError(err)) return null;
  const ax = err as AxiosError<SequenceEnrollmentConflictResponse>;
  if (ax.response?.status === 409 && ax.response.data) {
    return ax.response.data;
  }
  return null;
}


// ---------------------------------------------------------------------------
// Contacts — active-enrollment lookup (used by manual-enroll dialog + the
// LT-179 contact detail panel).
// ---------------------------------------------------------------------------

export const contactSequenceAPI = {
  getActiveEnrollment: async (
    contactId: string,
  ): Promise<ContactActiveEnrollmentResponse> => {
    const r = await client.get<ContactActiveEnrollmentResponse>(
      `/contacts/${contactId}/active-enrollment`,
    );
    return r.data;
  },
};
